import React, { useMemo, useState } from "react";
import {
  Breadcrumbs,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Link,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";

import clsx from "clsx";

import NavigateNextIcon from "@material-ui/icons/NavigateNext";

import { debounce } from "lodash";

import Navigation from "../Navigation";

import useFetchCatalogedTrackers from "./queries/useFetchCatalogedTrackers";
import WebTrackerCatalogTable, {
  WebTrackerCatalogTableSort,
} from "./WebTrackerCatalogPage/WebTrackerCatalogTable";

import { NamedTrackerInfo, webTrackerRiskLevelAsInt } from "./types";
import useWTMURLs from "./useWTMURLs";

const useStyles = makeStyles((_) => ({
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
  card: {
    minWidth: 800,
    minHeight: 500,
    position: "relative",
  },
  cardContent: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  cardNav: {
    padding: "1em",
  },
  columnMainContent: {
    padding: 0,
    width: "100%",
  },
  columnMainContentInner: {
    padding: "1em",
  },
  darkText: {
    color: "#263238",
  },
  description: {
    marginBottom: "1em",
    marginTop: "1em",
  },
  lightText: {
    color: "#546E7A",
  },
  link: {
    cursor: "pointer",
  },
  navigation: {
    paddingTop: 12,
  },
  pageTitle: {
    paddingTop: 12,
  },
}));

const sortByNameFn = (sort: WebTrackerCatalogTableSort) => {
  return (a: NamedTrackerInfo, b: NamedTrackerInfo) => {
    const aName = a.name || "";
    const bName = b.name || "";

    return sort.direction === "asc"
      ? aName.localeCompare(bName)
      : bName.localeCompare(aName);
  };
};

const sortByRiskFn = (sort: WebTrackerCatalogTableSort) => {
  return (a: NamedTrackerInfo, b: NamedTrackerInfo) => {
    const aRisk = webTrackerRiskLevelAsInt(a.riskLevel);
    const bRisk = webTrackerRiskLevelAsInt(b.riskLevel);

    const byRisk = sort.direction === "asc" ? aRisk - bRisk : bRisk - aRisk;

    if (byRisk !== 0) {
      return byRisk;
    }

    return sortByNameFn({ ...sort, direction: "desc" })(a, b);
  };
};

function WebTrackerCatalogPage() {
  const classes = useStyles();
  const { data, isLoading } = useFetchCatalogedTrackers();
  const [searchString, setSearchString] = useState<string>("");
  const [searchStringDisplay, setSearchStringDisplay] = useState<string>("");
  const [sort, setSort] = useState<WebTrackerCatalogTableSort>({
    column: "name",
    direction: "asc",
  });

  const onSearch = debounce((searchString: string) => {
    setSearchString(searchString.trim().toLowerCase());
  }, 500);

  const onSearchDisplay = (searchString: string) => {
    setSearchStringDisplay(searchString);
    onSearch(searchString);
  };

  const trackers = useMemo(() => {
    return data?.trackers ?? [];
  }, [data?.trackers]);

  const filteredTrackers = useMemo(() => {
    if (searchString === "") {
      return trackers;
    }

    return trackers.filter((t) => {
      const searchTarget = `${t.name} ${t.description} ${t.category}`
        .trim()
        .toLowerCase();

      return searchTarget.includes(searchString);
    });
  }, [trackers, searchString]);

  const sortedTrackers = useMemo(() => {
    if (sort.column === "name") {
      return filteredTrackers.sort(sortByNameFn(sort));
    }

    return filteredTrackers.sort(sortByRiskFn(sort));
  }, [filteredTrackers, sort]);

  const wtmURLs = useWTMURLs();

  return (
    <Navigation className={clsx(classes.navigation)}>
      <div className={classes.pageTitle}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className={clsx(classes.breadcrumb)}
        >
          <Link color="inherit" href={wtmURLs.trackersOverview}>
            <Typography
              className={clsx(classes.link, classes.lightText)}
              variant="overline"
            >
              Web Tracker Manager
            </Typography>
          </Link>
          <Typography
            className={clsx(classes.link, classes.darkText)}
            variant="overline"
          >
            Web Tracker Catalog
          </Typography>
        </Breadcrumbs>
      </div>

      <Card className={classes.card}>
        <>
          <CardHeader
            title={
              isLoading
                ? "Loading..."
                : `Showing ${filteredTrackers.length} of ${trackers.length} Web Trackers`
            }
          />
          <Divider />
          <CardContent className={classes.cardContent}>
            <nav className={classes.cardNav}>
              <Grid
                container
                spacing={1}
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    placeholder="Search..."
                    type="search"
                    value={searchStringDisplay}
                    variant="outlined"
                    onChange={(e) => onSearchDisplay(e.target.value)}
                  />
                </Grid>
              </Grid>
            </nav>

            <WebTrackerCatalogTable
              trackers={sortedTrackers}
              sort={sort}
              onSort={(newSortColumn) => {
                const newSortDirection =
                  newSortColumn === sort.column
                    ? sort.direction === "asc"
                      ? "desc"
                      : "asc"
                    : "asc";

                setSort({
                  column: newSortColumn,
                  direction: newSortDirection,
                });
              }}
            />
          </CardContent>
        </>
      </Card>
    </Navigation>
  );
}

export default WebTrackerCatalogPage;
